import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  MenuItem,
  Stack,
  Switch,
  Table,
  TableBody,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CustomAutoComplete,
  CustomButton,
  CustomSelect,
  DialogBody,
  DialogContainer,
  DialogTitle,
  GeneralTableBox,
  HeadTableCell,
  HorizontalForm,
  NoDataSkeleton,
  RowTableCell,
} from '../../../common/components';
import {
  useAddNotificationConfigUser,
  useUpdateNotificationConfig,
  useUpdateNotificationConfigUser,
} from '../../../hooks/useNotifications';
import { useUsers } from '../../../hooks/useUsers';
import { CHANNEL_LABELS } from '../constants/notificationsConstants';

export const RECIPIENT_STRATEGY_LABELS = {
  module_audience: 'Audiencia por módulo',
  manual_users: 'Usuarios seleccionados',
  mixed: 'Audiencia + usuarios',
};

export const NotificationUsersDialog = ({ open, configItem, onClose }) => {
  const [selectedUserUuid, setSelectedUserUuid] = useState('');
  const [strategy, setStrategy] = useState('manual_users');
  const { data: usersData = [], isLoading: usersLoading } = useUsers();
  const addConfigUser = useAddNotificationConfigUser();
  const updateConfigUser = useUpdateNotificationConfigUser();
  const updateConfig = useUpdateNotificationConfig();

  const users = Array.isArray(usersData) ? usersData : usersData?.results || [];
  const configUsers = configItem?.users;
  const notificationUsers = useMemo(() => configUsers || [], [configUsers]);
  const existingUserUuids = useMemo(
    () => new Set(notificationUsers.map((notificationUser) => notificationUser.uuid_user)),
    [notificationUsers]
  );
  const availableUsers = users.filter((user) => !existingUserUuids.has(user.uuid));
  const isSaving = addConfigUser.isPending || updateConfigUser.isPending || updateConfig.isPending;

  useEffect(() => {
    if (!open || !configItem) return;
    setStrategy(configItem.recipient_strategy || 'manual_users');
    setSelectedUserUuid('');
  }, [open, configItem]);

  const handleStrategyChange = (event) => {
    const nextStrategy = event.target.value;
    setStrategy(nextStrategy);
    updateConfig.mutate({
      uuid: configItem.uuid,
      payload: { recipient_strategy: nextStrategy },
    });
  };

  const handleAddUser = async () => {
    if (!selectedUserUuid || !configItem) return;

    if (!['manual_users', 'mixed'].includes(strategy)) {
      setStrategy('manual_users');
      await updateConfig.mutateAsync({
        uuid: configItem.uuid,
        payload: { recipient_strategy: 'manual_users' },
      });
    }

    await addConfigUser.mutateAsync({
      uuidConfig: configItem.uuid,
      payload: {
        uuid_user: selectedUserUuid,
        channel: 'internal_panel',
        is_active: true,
        receives_in_app: true,
        receives_email: false,
      },
    });
    setSelectedUserUuid('');
  };

  const handleToggleUser = (notificationUser, field, value) => {
    updateConfigUser.mutate({
      uuid: notificationUser.uuid,
      payload: { [field]: value },
    });
  };

  return (
    <DialogContainer open={open} onClose={onClose} size="medium" minHeight="auto">
      <DialogTitle>Usuarios destinatarios</DialogTitle>
      <DialogBody sx={{ display: 'block' }}>
        <Box sx={{ mb: 1 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {configItem?.display_name || 'Notificación'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Estos usuarios reciben la notificación en la campanita del sistema.
            </Typography>
          </Stack>
        </Box>

        <HorizontalForm withBorder={false} sx={{ p: 1, gap: 2, alignItems: 'center' }}>
          <CustomSelect
            label="Estrategia"
            value={strategy}
            onChange={handleStrategyChange}
            disabled={!configItem || isSaving}
          >
            {Object.entries(RECIPIENT_STRATEGY_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </CustomSelect>
          <CustomAutoComplete
            label="Agregar usuario"
            value={selectedUserUuid}
            onChange={(event) => setSelectedUserUuid(event.target.value)}
            disabled={usersLoading || isSaving}
            loading={usersLoading}
            options={availableUsers}
            optionValueKey="uuid"
            placeholder="Buscar usuario"
            forcePopupIcon={false}
            noOptionsText="Sin usuarios disponibles"
            getOptionLabel={(option) =>
              option ? `${option.names || 'Usuario'} - ${option.email || option.uuid}` : ''
            }
          />
          <CustomButton
            variant="base"
            onClick={handleAddUser}
            disabled={!selectedUserUuid || isSaving}
            sx={{ minWidth: 160, mt: 1.5 }}
          >
            Agregar
          </CustomButton>
        </HorizontalForm>

        <Box sx={{ px: 1, pt: 1 }}>
          <GeneralTableBox>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <HeadTableCell>Usuario</HeadTableCell>
                  <HeadTableCell>Canal</HeadTableCell>
                  <HeadTableCell>In-app</HeadTableCell>
                  <HeadTableCell>Email</HeadTableCell>
                  <HeadTableCell>Activo</HeadTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificationUsers.length === 0 && (
                  <NoDataSkeleton colSpan={5} message="Todavía no hay usuarios seleccionados." />
                )}
                {notificationUsers.map((notificationUser) => (
                  <TableRow key={notificationUser.uuid} hover>
                    <RowTableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {notificationUser.user_name || 'Usuario'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {notificationUser.user_email || notificationUser.uuid_user}
                      </Typography>
                    </RowTableCell>
                    <RowTableCell>
                      {CHANNEL_LABELS[notificationUser.channel] || notificationUser.channel}
                    </RowTableCell>
                    <RowTableCell>
                      <Switch
                        checked={Boolean(notificationUser.receives_in_app)}
                        onChange={(event) =>
                          handleToggleUser(
                            notificationUser,
                            'receives_in_app',
                            event.target.checked
                          )
                        }
                        disabled={isSaving}
                      />
                    </RowTableCell>
                    <RowTableCell>
                      <Switch
                        checked={Boolean(notificationUser.receives_email)}
                        onChange={(event) =>
                          handleToggleUser(notificationUser, 'receives_email', event.target.checked)
                        }
                        disabled={isSaving}
                      />
                    </RowTableCell>
                    <RowTableCell>
                      <Switch
                        checked={Boolean(notificationUser.is_active)}
                        onChange={(event) =>
                          handleToggleUser(notificationUser, 'is_active', event.target.checked)
                        }
                        disabled={isSaving}
                      />
                    </RowTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </GeneralTableBox>
        </Box>

        <HorizontalForm withBorder={false} sx={{ p: 1, justifyContent: 'flex-end' }}>
          <CustomButton variant="warning" onClick={onClose}>
            Cerrar
          </CustomButton>
        </HorizontalForm>
      </DialogBody>
    </DialogContainer>
  );
};
